/**
 * Cross-space transfer: "move" a note or subtree between the personal
 * workspace and a shared folder (either direction, or folder to folder).
 *
 * Under the hood this is a copy + trash, modeled on migrate.ts: fresh ids in
 * the target space (so a doc name can never resurrect an old server doc),
 * content copied as CRDT STATE (encodeStateAsUpdate -> applyUpdate into the
 * brand-new doc; the one safe kind of seed), then the original is trashed
 * only after the target space acknowledges every doc. On timeout nothing is
 * trashed: worst case is a duplicate, never a loss.
 */
import * as Y from "yjs";
import { DOC_KEYS, newId, taskItems } from "@hydra-notes/shared";
import { toast } from "@/ui/toast";
import type { DataContext } from "./store";

const ACK_TIMEOUT_MS = 30_000;

export interface TransferPlan {
  sourceCtx: DataContext;
  targetCtx: DataContext;
  rootId: string;
  /** Where in the target tree the root lands. */
  targetParentId: string | null;
}

export async function transferItem(plan: TransferPlan): Promise<boolean> {
  const { sourceCtx, targetCtx, rootId, targetParentId } = plan;
  const src = sourceCtx.workspace;
  const root = src.getItem(rootId);
  if (!root) return false;

  // depth-first list of the subtree, parents before children
  const subtree: string[] = [];
  const walk = (id: string) => {
    subtree.push(id);
    for (const child of src.getSnapshot().filter((i) => i.parentId === id)) walk(child.id);
  };
  walk(rootId);

  const idMap = new Map<string, string>(subtree.map((id) => [id, newId()]));

  await targetCtx.workspace.whenLoaded;

  // 1) copy note contents as CRDT state into fresh target docs (content
  //    before tree entries, same as create_note: a failure here leaves
  //    invisible orphan docs, not broken tree items)
  const noteIds = subtree.filter((id) => src.getItem(id)?.type === "note");
  const openHandles: { release: () => void }[] = [];
  try {
    for (const noteId of noteIds) {
      const srcHandle = sourceCtx.notes.open(noteId);
      await srcHandle.whenLoaded;
      const state = Y.encodeStateAsUpdate(srcHandle.doc);
      const hasContent = srcHandle.doc.getText(DOC_KEYS.noteContent).length > 0;
      srcHandle.release();
      const dstHandle = targetCtx.notes.open(idMap.get(noteId)!);
      openHandles.push(dstHandle);
      await dstHandle.whenLoaded;
      if (hasContent) Y.applyUpdate(dstHandle.doc, state, "transfer");
    }

    // 1b) task-list docs the same way, with two fields STRIPPED per task:
    //     seq (bound to the source's (space,list,task) projection row; the
    //     target space renumbers) and nothing else. The builtin flag is
    //     stripped at the tree layer below (a copy is never the default).
    const taskListIds = subtree.filter((id) => src.getItem(id)?.type === "tasklist");
    for (const listId of taskListIds) {
      const srcHandle = sourceCtx.tasks.open(listId);
      await srcHandle.whenLoaded;
      const state = Y.encodeStateAsUpdate(srcHandle.doc);
      const hasTasks = taskItems(srcHandle.doc).size > 0;
      srcHandle.release();
      const dstHandle = targetCtx.tasks.open(idMap.get(listId)!);
      openHandles.push(dstHandle);
      await dstHandle.whenLoaded;
      if (hasTasks) {
        Y.applyUpdate(dstHandle.doc, state, "transfer");
        dstHandle.doc.transact(() => {
          taskItems(dstHandle.doc).forEach((m) => m.delete("seq"));
        }, "transfer");
      }
    }

    // 2) tree entries with remapped ids and parents
    targetCtx.workspace.doc.transact(() => {
      for (const id of subtree) {
        const item = src.getItem(id);
        if (!item) continue;
        const m = new Y.Map<unknown>();
        m.set("type", item.type);
        m.set("parentId", id === rootId ? targetParentId : idMap.get(item.parentId ?? "") ?? null);
        m.set("title", item.title);
        m.set("sortKey", item.sortKey);
        m.set("tags", item.tags);
        m.set("createdAt", item.createdAt);
        m.set("updatedAt", Date.now());
        if (item.folderNoteId && idMap.has(item.folderNoteId)) m.set("folderNoteId", idMap.get(item.folderNoteId));
        if (item.aliases?.length) m.set("aliases", item.aliases);
        if (item.colorKey) m.set("colorKey", item.colorKey);
        // deliberately NOT copied: builtin (a transferred copy is never the
        // default list; the source keeps or re-seeds its own)
        targetCtx.workspace.items.set(idMap.get(id)!, m);
      }
    });

    // 3) wait for the target space to acknowledge everything; only then is
    //    it safe to trash the source (online-only targets mean the server
    //    truly has it once whenSynced + flush of dirty docs settles)
    const acked = await waitForTargetAck(targetCtx, ACK_TIMEOUT_MS);
    if (!acked) {
      toast.error("The move did not finish uploading. Nothing was removed; try again once connected.");
      return false;
    }

    // 4) trash the original (the source keeps its 30-day recovery path)
    src.trash(rootId);
    return true;
  } finally {
    for (const h of openHandles) h.release();
  }
}

function waitForTargetAck(ctx: DataContext, timeoutMs: number): Promise<boolean> {
  if (!ctx.sync) return Promise.resolve(true); // local target: nothing to ack
  const sync = ctx.sync;
  return new Promise((resolve) => {
    const start = Date.now();
    const timer = setInterval(() => {
      if (!sync.hasPendingFor(ctx.workspaceId)) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 300);
  });
}
